<template>
	<div
		:class="[
			$style['app-textarea-field-container'],
			{
				[$style['app-textarea-field-container--multiple']]: multiple
			}
		]"
	>
		<div
			:class="[
				$style['app-textarea-field'],
				$style['app-textarea-field--dense'],
				{
					[$style['app-textarea-field--disabled']]: isDisabled,
					[$style['app-textarea-field--no-helper']]: !helperText && !maxCharacters,
					[$style['app-textarea-field--invalid']]: invalid || maxCharacters && symbolsCount > maxCharacters,
					[$style['app-textarea-field--focused']]: isFocused
				}
			]"
		>
			<textarea
				v-bind="$attrs"
				:class="$style['app-textarea-field__input']"
				:value="value"
				@focus="inputFocusHandler"
				@blur="inputBlurHandler"
				@input="inputInputHandler"
				@keydown="inputKeyDownHandler"
			/>
			<FloatingLabel
				v-if="labelText"
				:class="$style['app-textarea-field__label']"
				:label-text="labelText"
				:required="isRequired"
				:is-focused="isFocused"
				:is-floated="isFloated"
			/>
			<label
				v-if="!!!value"
				:class="$style['app-textarea-field__placeholder']"
			>
				{{ placeholder }}
			</label>
			<NotchedOutline
				:class="$style['app-textarea-field__outline']"
				:is-focused="isFocused"
				:is-floated="isFloated && !!labelText"
			/>
			<NotchedOutlineIdle
				:class="$style['app-textarea-field__idle']"
				:is-focused="isFocused"
				:is-floated="isFloated && !!labelText"
			/>
		</div>
		<p
			v-if="helperText || maxCharacters"
			:class="$style['app-textarea-field-helper-text']"
		>
			<template v-if="helperText">
				{{ helperText }}
			</template>
			<span
				v-if="maxCharacters"
				:class="$style['app-textarea-field-helper-text--right']"
			>
				{{ symbolsCount }} / {{ maxCharacters }}
			</span>
		</p>
	</div>
</template>
<script>
import NotchedOutline from '@c:essentials/NotchedOutline'
import NotchedOutlineIdle from '@c:essentials/NotchedOutlineIdle'
import FloatingLabel from '@c:essentials/FloatingLabel'
import requiredField from '@/mixins/requiredField'
export default {
	name: 'BaseTextArea',
	components: {
		NotchedOutline,
		NotchedOutlineIdle,
		FloatingLabel
	},
	mixins: [requiredField],
	inheritAttrs: false,
	props: {
		value: {
			type: [String, Number],
			default: ''
		},
		labelText: {
			type: String,
			default: ''
		},
		required: {
			type: [Boolean, String],
			default: false
		},
		placeholder: {
			type: [String, Number],
			default: ''
		},
		helperText: {
			type: String,
			default: null
		},
		multiple: {
			type: [Boolean, String],
			default: false
		},
		invalid: {
			type: [Boolean, String],
			default: false
		},
		height: {
			type: String,
			default: '115px'
		},
		maxCharacters: {
			type: Number,
			default: 0
		}
	},
	data: () => ({
		isFocused: false,
		isFloated: false
	}),
	computed: {
		isDisabled () {
			return this.$attrs.disabled === '' || this.$attrs.disabled
		},
		symbolsCount () {
			return this.value ? this.value.length : 0
		}
	},
	watch: {
		value: {
			immediate: true,
			handler () {
				if (this.value !== null && this.value !== '' || this.placeholder) {
					this.isFloated = true
				} else {
					this.isFloated = this.isFocused
				}
			}
		},
		placeholder: {
			immediate: true,
			handler () {
				if (this.placeholder) {
					this.isFloated = true
				}
			}
		}
	},
	methods: {
		inputFocusHandler (event) {
			this.isFocused = true
			this.isFloated = true
			this.$emit('focus', event)
		},
		inputBlurHandler (event) {
			if (!this.isFocused) return
			this.isFocused = false
			this.isFloated = !!(this.value || this.placeholder)
			this.$emit('blur', event)
		},
		inputInputHandler (e) {
			this.$emit('input', e.target.value)
		},
		inputKeyDownHandler (event) {
			if (event.keyCode === 13) {
				this.$emit('keydown', event)
				event.target.blur()
				return
			}
			if (this.maxCharacters && event.target.value.length >= this.maxCharacters) {
				if ([46, 8, 9, 27, 13, 110, 190].includes(event.keyCode) ||
					((event.keyCode === 65 || event.keyCode === 86 || event.keyCode === 67) && (event.ctrlKey === true || event.metaKey === true)) ||
					(event.keyCode >= 35 && event.keyCode <= 40))
				{
					return
				}
				if (event.keyCode >= 48 && event.keyCode <= 57 || event.keyCode >= 65 && event.keyCode <= 90) {
					event.preventDefault()
					return false
				}
			}
			this.$emit('keydown', event)
		}
	}
}
</script>
<style lang="scss" module>
.app-textarea-field-container {
    width: 100%;
}

.app-textarea-field-helper-text {
    display: flex;
    align-items: center;
    padding: 0 $gutter;
    margin: 0;
    margin-bottom: calc($gutter / 2);
    color: getColor(helper-text);
    font-size: 12px;
    line-height: 1.25rem;
    width: 100%;

    &--right {
        text-align: right;
        justify-self: flex-end;
        flex: 1;
    }
}

.app-textarea-field {
    position: relative;
    display: inline-flex;
    width: 100%;
    align-items: flex-end;
    box-sizing: border-box;
    margin-top: $gutter;
    border: none;

    &:not(.app-textarea-field--disabled, .app-textarea-field--focused):hover {
        .app-textarea-field__outline {
            border-color: getColor(primary);
        }

        .app-textarea-field__idle {
            border-color: getColor(text);
        }
    }

    &__label {
        z-index: 1;
    }

    &__placeholder {
        z-index: 1;
        font-size: 0.813rem;
        line-height: 1.2;
        font-weight: 400;
        top: 20px;
        letter-spacing: 0.0094em;
        position: absolute;
        left: 20px;
        right: auto;
        text-overflow: ellipsis;
        white-space: nowrap;
        cursor: text;
        overflow: hidden;
        color: getColor(helper-text);
    }

    &__input {
        resize: none;
        z-index: 2;
        display: flex;
        box-sizing: border-box;
        border: none;
        border-radius: 0;
        background-color: transparent;
        font-size: 16px;
        line-height: 1.75rem;
        font-weight: 400;
        letter-spacing: 0.0094em;
        color: getColor(text) !important;
        width: 100%;
        height: 100%;
        transition: opacity 0.18s cubic-bezier(0.4, 0, 0.2, 1);
        caret-color: getColor(tertiary);

        &:focus {
            outline: none;
        }

        &:-webkit-autofill,
        &:-webkit-autofill:hover,
        &:-webkit-autofill:focus {
            animation-name: autofill;
            animation-fill-mode: both;
            -webkit-text-fill-color: getColor(text) !important;
        }
    }

    &--disabled {
        border-radius: 5px;

        .app-textarea-field__idle {
            background-color: getColor(input-background-disabled);
        }

        .app-textarea-field__outline {
            background-color: getColor(input-background-disabled);
        }

        .app-textarea-field__label {
            color: getColor(input-label-disabled);
        }

        .app-textarea-field__input {
            color: getColor(helper-text) !important;
        }
    }

    &:not(.app-textarea-field--disabled) {
        & .app-textarea-field__idle {
            background-color: getColor(input-background);
        }

        & .app-textarea-field__outline {
            background-color: getColor(input-background);
        }
    }

    &--dense {
        height: 100%;
        padding: $gutter $gutter * 2;
    }

    &--no-helper {
        margin: $gutter 0;
        margin-bottom: $gutter;
    }

    &--invalid {
        .app-textarea-field__input {
            caret-color: getColor(danger);
        }

        .app-textarea-field__outline {
            border-color: getColor(danger) !important;
        }

        .app-textarea-field__idle {
            border-color: getColor(danger) !important;
        }

        .app-textarea-field__label {
            color: getColor(input-label-disabled) !important;
        }

        & ~ .app-textarea-field-helper-text {
            color: getColor(danger) !important;
        }
    }
}

@keyframes autofill {
    to {
        color: inherit;
        background: transparent;
    }
}
</style>
